import React, { useRef, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { formatDate } from '../../utils/dateFormatter';
import { riyadhDayIso } from '../../utils/dateValueHelper';
import { TEXT, errorText } from '../../config/listingPerformanceLabels';
import { useCreateChange, useUpdateChange } from '../../hooks/useListingPerformance';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import ChangeImpact from './ChangeImpact';

/**
 * «أداء الوحدات» — one unit's change log (the sheet's «التغييرات», 11 fields). Ops add and edit rows;
 * an experiment is a row with a hypothesis and a review date. Rows imported from the sheet are marked.
 * Each row carries the server's counted box; when its period has ended and «النتيجة» is empty, the log and the first
 * such row open by themselves, so the follow-up is in front of ops (tasks/change-impact-before-after.md).
 * In the owner view the server sends date + change only, and this shows nothing else.
 */

// The platforms a change can be made on — the server's list (models/ListingChange.js), in its order.
const PLATFORMS = ['airbnb', 'bookingcom', 'gathern'];
// Each name isolated on its own, so a Latin name never flips the order of the Arabic list.
const PlatformNames = ({ list }) => list.map((p, i) => (
  <React.Fragment key={p}>{i > 0 && '، '}<bdi>{getPlatformDisplayName(p)}</bdi></React.Fragment>
));

const FIELDS = ['change', 'hypothesis', 'settingBefore', 'settingAfter', 'guestPriceBefore', 'guestPriceAfter', 'otherFactors', 'result'];
const LONG = new Set(['change', 'hypothesis', 'otherFactors', 'result', 'settingBefore', 'settingAfter']);
// Folded under «تفاصيل إضافية» with the hypothesis and review date (founder 1 Nov): a plain change needs only its
// date and text. Those two lead the fold because together they make an experiment.
const MORE_AFTER_REVIEW = ['settingBefore', 'settingAfter', 'otherFactors', 'result'];
// Opens the fold on a row that uses it — filled data is never hidden.
const hasMore = (row) => Boolean(row) && ['hypothesis', 'reviewDate', ...MORE_AFTER_REVIEW].some(f => row[f]);

// The change date is a business date — today in Riyadh, not in the browser's timezone.
const emptyForm = () => ({ date: riyadhDayIso(new Date()), reviewDate: '', platforms: [], ...Object.fromEntries(FIELDS.map(f => [f, ''])) });
const formOf = (row) => ({
  date: row.date,
  reviewDate: row.reviewDate || '',
  platforms: [...(row.platforms || [])],
  ...Object.fromEntries(FIELDS.map(f => [f, row[f] ?? '']))
});

const FieldLabel = ({ field, required = false }) => (
  <Form.Label>
    {TEXT.changeFields[field]}{' '}
    {required
      ? <span className="text-danger" aria-hidden="true">*</span>
      : <span className="lp-form__optional">({TEXT.optional})</span>}
  </Form.Label>
);

const TextField = ({ field, form, set, inputRef }) => (
  <Form.Group controlId={`lp-change-${field}`} className={LONG.has(field) ? '' : 'lp-form__half'}>
    <FieldLabel field={field} required={field === 'change'} />
    <Form.Control
      ref={inputRef}
      as={LONG.has(field) ? 'textarea' : 'input'}
      rows={field === 'change' ? 3 : 2}
      value={form[field]}
      onChange={set(field)}
      required={field === 'change'}
      maxLength={LONG.has(field) ? 2000 : 200}
    />
  </Form.Group>
);

/**
 * Which platforms the change was made on: real checkboxes, shown as chips. Required on a new row; a row from before
 * the pick may stay without one — the server's rule (changeLogService.clean).
 */
const PlatformPick = ({ value, onChange, required }) => {
  const toggle = (p) => onChange(value.includes(p) ? value.filter(x => x !== p) : PLATFORMS.filter(x => x === p || value.includes(x)));
  return (
    <fieldset className="lp-form__platforms" aria-describedby="lp-change-platforms-hint">
      <legend className="form-label">
        {TEXT.changeFields.platforms}{' '}
        {required
          ? <><span className="text-danger" aria-hidden="true">*</span><span className="visually-hidden">{TEXT.required}</span></>
          : <span className="lp-form__optional">({TEXT.optional})</span>}
      </legend>
      <div className="lp-platform-picks">
        {PLATFORMS.map(p => (
          <label key={p} className="lp-platform-pick">
            <input type="checkbox" className="lp-platform-pick__input" checked={value.includes(p)} onChange={() => toggle(p)} />
            <span className="lp-platform-pick__face">
              <i className={`bi ${value.includes(p) ? 'bi-check2' : 'bi-plus'}`} aria-hidden="true" />
              <bdi>{getPlatformDisplayName(p)}</bdi>
            </span>
          </label>
        ))}
      </div>
      <span id="lp-change-platforms-hint" className="lp-form__hint">{TEXT.platformsHint}</span>
    </fieldset>
  );
};

const FORM_ID = 'lp-change-form';

const ChangeForm = ({ show, onHide, unitCode, row, focusResult = false }) => {
  const create = useCreateChange();
  const update = useUpdateChange();
  const [form, setForm] = useState(() => (row ? formOf(row) : emptyForm()));
  // «اكتب النتيجة» lands on that field: it sits last in the fold.
  const [more, setMore] = useState(() => focusResult || hasMore(row));
  const resultRef = useRef(null);
  const [error, setError] = useState(null);
  const busy = create.isPending || update.isPending;
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  // A new row needs a platform; so does one that already has some (un-ticking them all is refused on the server).
  const platformsRequired = !row || Boolean(row.platforms?.length);
  const missingPlatform = platformsRequired && !form.platforms.length;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const body = { ...form, reviewDate: form.reviewDate || null };
    FIELDS.forEach(f => { body[f] = body[f].trim() ? body[f] : null; });
    try {
      if (row) await update.mutateAsync({ id: row.id, changes: body });
      else await create.mutateAsync({ unitCode, ...body });
      onHide();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Modal
      show={show}
      onHide={busy ? undefined : onHide}
      // After it has opened: the modal takes focus for itself as it opens, so an earlier focus on the field is lost.
      onEntered={() => { if (focusResult) resultRef.current?.focus(); }}
      centered
      scrollable
      fullscreen="sm-down"
      dir="rtl"
      size="lg"
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title className="lp-modal__title">{row ? TEXT.editChange : TEXT.addChange} — {unitCode}</Modal.Title>
      </Modal.Header>
      {/* The form stays inside the body: Bootstrap's scrollable modal needs the body directly under the modal, or a
          long form is cut off with the buttons out of reach. The footer's button reaches the form by id. */}
      <Modal.Body>
        <Form id={FORM_ID} className="lp-form" onSubmit={submit}>
          <div className="lp-form__row">
            <Form.Group controlId="lp-change-date">
              <FieldLabel field="date" required />
              <Form.Control type="date" value={form.date} onChange={set('date')} required />
            </Form.Group>
          </div>
          <PlatformPick value={form.platforms} onChange={(platforms) => setForm(f => ({ ...f, platforms }))} required={platformsRequired} />
          <TextField field="change" form={form} set={set} />
          <TextField field="guestPriceBefore" form={form} set={set} />
          <TextField field="guestPriceAfter" form={form} set={set} />
          <div>
            <button
              type="button"
              className="lp-form__more-toggle"
              aria-expanded={more}
              aria-controls="lp-change-more"
              onClick={() => setMore(m => !m)}
            >
              <i className={`bi ${more ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />
              {TEXT.moreDetails}
            </button>
          </div>
          {more && (
            <div id="lp-change-more" className="lp-form__more">
              <TextField field="hypothesis" form={form} set={set} />
              <div className="lp-form__row">
                <Form.Group controlId="lp-change-review">
                  <FieldLabel field="reviewDate" />
                  <Form.Control type="date" value={form.reviewDate} onChange={set('reviewDate')} />
                  <span className="lp-form__hint">{TEXT.reviewDateHint}</span>
                </Form.Group>
              </div>
              {MORE_AFTER_REVIEW.map(f => <TextField key={f} field={f} form={form} set={set} inputRef={f === 'result' ? resultRef : undefined} />)}
            </div>
          )}
          {error && <div className="lp-error" role="alert">{error}</div>}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>{TEXT.cancel}</Button>
        <Button type="submit" form={FORM_ID} variant="primary" disabled={busy || !form.change.trim() || !form.date || missingPlatform}>
          {busy && <Spinner animation="border" size="sm" className="lp-spin" />} {TEXT.save}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const FACTS = ['hypothesis', 'settingBefore', 'settingAfter', 'guestPriceBefore', 'guestPriceAfter', 'otherFactors'];
// The latest few answer "what changed lately?"; the rest is one press away.
const RECENT = 5;

/**
 * One change: a single line (date · tags · what changed) that opens to its facts. Ops read the list as
 * "what moved recently?", and open the one row that explains a jump on the chart.
 */
const ChangeRow = ({ c, ownerView, startOpen, onEdit, onWriteResult }) => {
  const impact = ownerView ? null : c.impact;
  const [open, setOpen] = useState(Boolean(startOpen));
  const platforms = ownerView ? [] : c.platforms || [];
  const hasFacts = !ownerView && (platforms.length > 0 || FACTS.some(f => c[f]) || c.reviewDate || c.result);
  const hasBody = hasFacts || Boolean(impact);
  return (
    <li className={`lp-change${c.hypothesis ? ' lp-change--experiment' : ''}${impact?.due ? ' lp-change--due' : ''}${open ? ' is-open' : ''}`}>
      <div className="lp-change__line">
        <button
          type="button"
          className="lp-change__toggle"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
        >
          <span className="lp-change__date">{formatDate(c.date, { year: undefined })}</span>
          {c.hypothesis && <span className="lp-change__tag"><i className="bi bi-flask" aria-hidden="true" /> {TEXT.experimentTag}</span>}
          {impact?.due && <span className="lp-change__tag lp-change__tag--due">{TEXT.resultNotWritten}</span>}
          <span className={`lp-change__text${open ? '' : ' lp-ellipsis'}`}>{c.change}</span>
          {(hasBody || !open) && <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} lp-change__chevron`} aria-hidden="true" />}
        </button>
        {!ownerView && (
          <Button variant="link" size="sm" className="lp-link" onClick={() => onEdit(c)} aria-label={`${TEXT.editChange} ${formatDate(c.date)}`}>
            <i className="bi bi-pencil" aria-hidden="true" />
          </Button>
        )}
      </div>
      {open && hasFacts && (
        <dl className="lp-change__facts">
          {platforms.length > 0 && (<><dt>{TEXT.changeFields.platforms}</dt><dd><PlatformNames list={platforms} /></dd></>)}
          {FACTS.filter(f => c[f]).map(f => (
            <React.Fragment key={f}>
              <dt>{TEXT.changeFields[f]}</dt>
              <dd>{c[f]}</dd>
            </React.Fragment>
          ))}
          {c.reviewDate && (<><dt>{TEXT.changeFields.reviewDate}</dt><dd>{formatDate(c.reviewDate)}</dd></>)}
          {c.result && (<><dt>{TEXT.changeFields.result}</dt><dd>{c.result}</dd></>)}
          {c.source === 'sheet_import' && (<><dt /><dd><span className="lp-change__tag lp-change__tag--muted">{TEXT.importedFromSheet}</span></dd></>)}
        </dl>
      )}
      {open && impact && <ChangeImpact impact={impact} onWriteResult={() => onWriteResult(c)} />}
    </li>
  );
};

const ChangeLog = ({ unitCode, changes = [], ownerView }) => {
  const [editing, setEditing] = useState(null); // 'new' | row
  const [focusResult, setFocusResult] = useState(false);
  const [all, setAll] = useState(false);
  // Staff: folded to its title and the latest date (founder 20 Oct — a cleaner page); adding stays one press.
  // The owner view keeps it open: what we changed is what the owner came to see. A change waiting for its result
  // opens it too: this is where the board's «نتائج لم تُكتب» leads.
  const [open, setOpen] = useState(() => Boolean(ownerView) || changes.some(c => c.impact?.due));
  // A row waiting for its result is never folded behind «عرض كل السجل», and stays in view after its result is saved,
  // so the row does not vanish the moment ops write. Only the first opens by itself: the others carry their tag, and
  // several open boxes would bury the log.
  const [pinned] = useState(() => new Set(changes.filter(c => c.impact?.due).map(c => c.id)));
  const shown = all ? changes : changes.filter((c, i) => i < RECENT || pinned.has(c.id));
  const firstDue = changes.find(c => c.impact?.due);
  const latest = changes[0] || null;

  return (
    <section className="lp-changes" aria-labelledby="lp-changes-title">
      <div className="lp-section-head">
        <div>
          <h4 id="lp-changes-title" className="lp-section-title">
            {ownerView || !changes.length ? (
              <>{TEXT.changeLog} {changes.length > 0 && <bdi className="lp-muted lp-num">({changes.length})</bdi>}</>
            ) : (
              <button
                type="button"
                className="lp-changes__toggle"
                aria-expanded={open}
                aria-controls="lp-changes-body"
                title={open ? TEXT.hideChangeLog : TEXT.showChangeLog}
                onClick={() => setOpen(o => !o)}
              >
                <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />
                {TEXT.changeLog} <bdi className="lp-muted lp-num">({changes.length})</bdi>
              </button>
            )}
          </h4>
          {!ownerView && open && <p className="lp-muted">{TEXT.changeLogHint}</p>}
          {!ownerView && !open && latest && (
            <p className="lp-muted">{TEXT.lastChange}: {formatDate(latest.date, { year: undefined })}</p>
          )}
        </div>
        {!ownerView && (
          <Button variant="outline-primary" size="sm" onClick={() => setEditing('new')}>
            <i className="bi bi-plus-lg" aria-hidden="true" /> {TEXT.addChange}
          </Button>
        )}
      </div>

      {!changes.length && <p className="lp-empty">{TEXT.noChanges}</p>}
      {changes.length > 0 && open && (
        <div id="lp-changes-body">
          <ol className="lp-changes__list">
            {shown.map((c, i) => (
              <ChangeRow
                key={c.id || `${c.date}-${i}`}
                c={c}
                ownerView={ownerView}
                startOpen={c === firstDue}
                onEdit={setEditing}
                onWriteResult={(row) => { setFocusResult(true); setEditing(row); }}
              />
            ))}
          </ol>
          {changes.length > RECENT && (
            <Button variant="link" size="sm" className="lp-link" onClick={() => setAll(a => !a)}>
              {all ? TEXT.showFewerChanges : <>{TEXT.showAllChanges} (<bdi className="lp-num">{changes.length}</bdi>)</>}
            </Button>
          )}
        </div>
      )}

      {editing && (
        <ChangeForm
          show
          onHide={() => { setEditing(null); setFocusResult(false); }}
          unitCode={unitCode}
          row={editing === 'new' ? null : editing}
          focusResult={focusResult}
        />
      )}
    </section>
  );
};

export default ChangeLog;
